import { type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Button } from './Button';
import { Icon, type IconName } from './Icon';
import styles from './ui.module.css';

export interface MenuItem {
  label: string;
  icon?: IconName;
  danger?: boolean;
  hidden?: boolean;
  onSelect(): void;
}

/** Overflow menu (menu-button pattern): arrow keys move, Escape closes and returns focus. */
export function Menu({
  label,
  items,
  trigger,
  avatar,
}: {
  label: string;
  items: MenuItem[];
  trigger?: ReactNode;
  /** Initials: renders a round avatar button instead of the ⋯ button. */
  avatar?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [flip, setFlip] = useState(false);
  const menuId = useId();
  const visible = items.filter((i) => !i.hidden);

  // Cards in the left column are too close to the screen edge for a menu that opens toward
  // it: when it would be cut off, open it the other way.
  useLayoutEffect(() => {
    if (!open) return setFlip(false);
    const r = listRef.current?.getBoundingClientRect();
    if (r && (r.left < 8 || r.right > window.innerWidth - 8)) setFlip(true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    rootRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const nodes = [...(rootRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const index = nodes.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'Escape') {
      e.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      nodes[(index + 1) % nodes.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      nodes[(index - 1 + nodes.length) % nodes.length]?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div className={styles.menuRoot} ref={rootRef} onKeyDown={onKeyDown}>
      {avatar !== undefined ? (
        <button
          ref={buttonRef}
          type="button"
          className={styles.avatarButton}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          aria-label={label}
          onClick={() => setOpen((v) => !v)}
        >
          {avatar}
        </button>
      ) : (
        <Button
          ref={buttonRef}
          variant="ghost"
          icon="more"
          iconOnly
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setOpen((v) => !v);
          }}
        >
          {label}
        </Button>
      )}
      {trigger}
      {open && (
        <ul
          ref={listRef}
          className={styles.menu}
          style={flip ? { insetInlineEnd: 'auto', insetInlineStart: 0 } : undefined}
          role="menu"
          id={menuId}
          aria-label={label}
        >
          {visible.map((item) => (
            <li key={item.label} role="none">
              <button
                type="button"
                role="menuitem"
                tabIndex={-1}
                className={[styles.menuItem, item.danger ? styles.menuDanger : ''].join(' ')}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setOpen(false);
                  item.onSelect();
                }}
              >
                {item.icon && <Icon name={item.icon} />}
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
